/**
 * QUIPWA — themes and display preferences
 *
 * Two kinds of state live here:
 *
 *   Themes (ThemeProfile)  colours, type, card, background. Presets are
 *                          read-only (presets.ts); the user's own themes are
 *                          replicated to every device through `_state.db`
 *                          (settings keys `theme.profile.<id>`).
 *   Device preferences     which theme is active, night mode, motion,
 *                          celebration, the skin with its thickness
 *                          (skin.ts) and sound (lib/sound). Per device: a
 *                          phone at night and a laptop by day want
 *                          different things.
 *
 * Everything is also cached in localStorage so the theme applies before React
 * mounts (theme-bootstrap.ts) with no flash.
 *
 * Editing a preset never forks it silently: the change goes to a `draft`,
 * applied live and announced in the editor, until the user names and saves
 * it (`saveDraft`) or discards it. At the limit of custom themes the save is
 * refused with a reason the UI shows — nothing is dropped quietly.
 *
 * ```ts
 * const store = useThemeStore.getState()
 * store.editActive((p) => ({ ...p, colors: { ...p.colors, primary: '#E07A5F' } }))  // 'saved' | 'draft'
 * store.saveDraft('Mi tema')                                                         // { ok: true } | { ok: false, reason: 'limit' }
 * ```
 */
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { DEFAULT_NIGHT_SCHEDULE, type NightMode, type NightSchedule } from './night'
import { DEFAULT_PROFILES, DEFAULT_PROFILE_ID, findPreset } from './presets'
import type { Skin, Thickness } from './skin'

export { DEFAULT_PROFILES } from './presets'

export const MAX_CUSTOM_PROFILES = 7
export const THEME_PROFILE_PREFIX = 'theme.profile.'

export type FlipAnimation = 'flip-h' | 'flip-v' | 'fade' | 'slide-up' | 'instant'
export type PatternStyle = 'splatter' | 'grid' | 'dots' | 'scanlines' | 'none'
export type MotionPreference = 'system' | 'reduced' | 'full'
export type CelebrationMode = 'none' | 'confetti' | 'custom-image'

export interface ThemeProfile {
  id: string
  name: string
  is_custom: boolean
  /** Preset this theme started from (for "restore section"). */
  base_id?: string | undefined
  /** ms of the last edit. */
  updated_at: number
  colors: {
    primary: string
    bgBase: string
    surface: string
    textTitle: string
    textBody: string
  }
  background: {
    type: 'solid' | 'gradient' | 'pattern' | 'image'
    value: string // hex, CSS gradient, or Drive file id
    patternStyle: PatternStyle
    opacity: number
    blur: number
  }
  typography: {
    titleFont: string
    bodyFont: string
    cardFont: string
    baseSize: number
    /** Flashcard text size, independent from the app's base size. */
    cardFontSize: number
  }
  effects: {
    cardRadius: number
    cardShadow: string
    globalOpacity: number
    flipAnimation: FlipAnimation
  }
}

export type SaveDraftResult = { ok: true; id: string } | { ok: false; reason: 'limit' | 'no-draft' }

export interface ThemeSettingsBackend {
  setSetting: <T>(key: string, value: T) => Promise<void>
  listSettings: <T>(prefix: string) => Promise<Array<{ key: string; value: T | null }>>
}

export interface ThemeState {
  activeProfileId: string
  customProfiles: ThemeProfile[]
  /** Unsaved edits to a preset; applied live until saved or discarded. */
  draft: ThemeProfile | null
  /** This device's custom themes have been copied to the replicated store. */
  migratedToReplica: boolean

  nightMode: NightMode
  nightSchedule: NightSchedule
  /**
   * Show a light version of the active theme (lib/theme/light.ts). A button,
   * not a theme: the theme is untouched. Wins over night mode.
   */
  lightMode: boolean
  motion: MotionPreference
  celebrationMode: CelebrationMode
  celebrationImageFileId: string | null
  /** The look of the controls: flat, or «Fichero» (docs/plan/piel-fichero.md). */
  skin: Skin
  /** How thick Fichero's keys and plates are drawn. Ignored by the flat skin. */
  thickness: Thickness
  /** Fichero's sounds (lib/sound/sound.ts): off until turned on. The flat skin is silent. */
  sound: boolean
  /** Their volume, 0–1. */
  soundVolume: number

  getActiveProfile: () => ThemeProfile
  setActiveProfile: (id: string) => void
  /** Change the active theme: saved if it is the user's, a draft if it is a preset. */
  editActive: (update: (profile: ThemeProfile) => ThemeProfile) => 'saved' | 'draft'
  saveDraft: (name: string) => SaveDraftResult
  discardDraft: () => void
  /** "New theme": a saved copy of the active theme. */
  duplicateActive: (name: string) => SaveDraftResult
  renameCustomProfile: (id: string, name: string) => void
  deleteCustomProfile: (id: string) => void
  /** Load the replicated themes (and copy this device's into them once). */
  hydrateFromReplica: (backend: ThemeSettingsBackend) => Promise<void>

  setNightMode: (mode: NightMode) => void
  setNightSchedule: (schedule: NightSchedule) => void
  setLightMode: (on: boolean) => void
  setMotion: (motion: MotionPreference) => void
  setCelebrationMode: (mode: CelebrationMode) => void
  setCelebrationImageFileId: (fileId: string | null) => void
  setSkin: (skin: Skin) => void
  setThickness: (thickness: Thickness) => void
  setSound: (on: boolean) => void
  /** Clamped to 0–1. */
  setSoundVolume: (volume: number) => void
}

let backendRef: ThemeSettingsBackend | null = null
const profileKey = (id: string) => `${THEME_PROFILE_PREFIX}${id}`

const REPLICATE_DELAY_MS = 600
const pendingWrites = new Map<string, ReturnType<typeof setTimeout>>()

/** Store a theme in the replicated settings. Debounced per theme: a slider fires dozens of edits per drag. */
function replicate(profile: ThemeProfile | null, id: string): void {
  const backend = backendRef
  if (!backend) return
  clearTimeout(pendingWrites.get(id))
  pendingWrites.set(id, setTimeout(() => {
    pendingWrites.delete(id)
    backend.setSetting(profileKey(id), profile).catch((err: unknown) => console.warn('[Theme] Could not store theme', err))
  }, profile === null ? 0 : REPLICATE_DELAY_MS))
}

/** Fill fields a theme written by an older version may lack. */
export function normalizeProfile(input: Partial<ThemeProfile> & { id: string }): ThemeProfile {
  const base = findPreset(input.base_id) ?? DEFAULT_PROFILES[0]!
  return {
    ...base,
    ...input,
    name: input.name || base.name,
    is_custom: input.is_custom ?? true,
    updated_at: input.updated_at ?? 0,
    colors: { ...base.colors, ...input.colors },
    background: { ...base.background, ...input.background },
    typography: { ...base.typography, ...input.typography },
    effects: { ...base.effects, ...input.effects },
  }
}

const byName = (a: ThemeProfile, b: ThemeProfile) => a.name.localeCompare(b.name)

/** What is cached in localStorage. */
type PersistedTheme = Pick<
  ThemeState,
  'activeProfileId' | 'customProfiles' | 'draft' | 'migratedToReplica' | 'nightMode' | 'nightSchedule' | 'lightMode' | 'motion' | 'celebrationMode' | 'celebrationImageFileId' | 'skin' | 'thickness' | 'sound' | 'soundVolume'
>

const PERSISTED_DEFAULTS: PersistedTheme = {
  activeProfileId: DEFAULT_PROFILE_ID,
  customProfiles: [],
  draft: null,
  migratedToReplica: false,
  nightMode: 'off',
  nightSchedule: DEFAULT_NIGHT_SCHEDULE,
  lightMode: false,
  motion: 'system',
  celebrationMode: 'none',
  celebrationImageFileId: null,
  skin: 'plana',
  thickness: 'clasico',
  sound: false,
  soundVolume: 0.5,
}

/** localStorage state written by older versions → the current shape. */
export function migrateThemeState(persisted: unknown, version: number): PersistedTheme {
  const old = (persisted ?? {}) as Partial<PersistedTheme> & { nightModeEnabled?: boolean }
  const { nightModeEnabled, ...rest } = old
  const merged: PersistedTheme = { ...PERSISTED_DEFAULTS, ...rest }
  if (version < 2) {
    // v1 had a boolean night filter and no draft/replica/motion fields.
    merged.nightMode = nightModeEnabled ? 'on' : 'off'
    merged.customProfiles = (rest.customProfiles ?? []).map((p) => normalizeProfile(p))
  }
  return merged
}
export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      ...PERSISTED_DEFAULTS,

      getActiveProfile: () => {
        const { draft, activeProfileId, customProfiles } = get()
        if (draft) return draft
        return findPreset(activeProfileId) ?? customProfiles.find((p) => p.id === activeProfileId) ?? DEFAULT_PROFILES[0]!
      },

      setActiveProfile: (id) => set({ activeProfileId: id, draft: null }),

      editActive: (update) => {
        const current = get().getActiveProfile()
        const next = { ...update(current), updated_at: Date.now() }
        if (current.is_custom && !get().draft) {
          set((s) => ({ customProfiles: s.customProfiles.map((p) => (p.id === next.id ? next : p)) }))
          replicate(next, next.id)
          return 'saved'
        }
        // A preset (or an existing draft): keep editing the draft.
        set({
          draft: {
            ...next,
            id: get().draft?.id ?? crypto.randomUUID(),
            is_custom: true,
            base_id: current.is_custom ? current.base_id : current.id,
          },
        })
        return 'draft'
      },

      saveDraft: (name) => {
        const { draft, customProfiles } = get()
        if (!draft) return { ok: false, reason: 'no-draft' }
        if (customProfiles.length >= MAX_CUSTOM_PROFILES) return { ok: false, reason: 'limit' }
        const saved = { ...draft, name: name.trim() || draft.name, updated_at: Date.now() }
        set({ customProfiles: [...customProfiles, saved].sort(byName), activeProfileId: saved.id, draft: null })
        replicate(saved, saved.id)
        return { ok: true, id: saved.id }
      },

      discardDraft: () => set({ draft: null }),

      duplicateActive: (name) => {
        const { customProfiles } = get()
        if (customProfiles.length >= MAX_CUSTOM_PROFILES) return { ok: false, reason: 'limit' }
        const source = get().getActiveProfile()
        const copy: ThemeProfile = {
          ...source,
          id: crypto.randomUUID(),
          name: name.trim() || source.name,
          is_custom: true,
          base_id: source.is_custom ? source.base_id : source.id,
          updated_at: Date.now(),
        }
        set({ customProfiles: [...customProfiles, copy].sort(byName), activeProfileId: copy.id, draft: null })
        replicate(copy, copy.id)
        return { ok: true, id: copy.id }
      },

      renameCustomProfile: (id, name) => {
        const trimmed = name.trim()
        const current = get().customProfiles.find((p) => p.id === id)
        if (!trimmed || !current) return
        const renamed = { ...current, name: trimmed, updated_at: Date.now() }
        set((s) => ({ customProfiles: s.customProfiles.map((p) => (p.id === id ? renamed : p)).sort(byName) }))
        replicate(renamed, id)
      },

      deleteCustomProfile: (id) => {
        set((s) => ({
          customProfiles: s.customProfiles.filter((p) => p.id !== id),
          activeProfileId: s.activeProfileId === id ? DEFAULT_PROFILE_ID : s.activeProfileId,
        }))
        replicate(null, id)
      },

      hydrateFromReplica: async (backend) => {
        backendRef = backend
        const rows = await backend.listSettings<ThemeProfile>(THEME_PROFILE_PREFIX)
        const remote = new Map<string, ThemeProfile | null>(
          rows.map((r) => [r.key.slice(THEME_PROFILE_PREFIX.length), r.value])
        )
        if (!get().migratedToReplica) {
          // First time on this device: its own themes join the replicated set.
          for (const local of get().customProfiles) {
            if (!remote.has(local.id)) {
              await backend.setSetting(profileKey(local.id), local)
              remote.set(local.id, local)
            }
          }
          set({ migratedToReplica: true })
        }
        const profiles = [...remote.entries()]
          .filter((e): e is [string, ThemeProfile] => e[1] !== null)
          .map(([id, p]) => normalizeProfile({ ...p, id }))
          .sort(byName)
          .slice(0, MAX_CUSTOM_PROFILES)
        const { activeProfileId } = get()
        const activeExists = !!findPreset(activeProfileId) || profiles.some((p) => p.id === activeProfileId)
        set({ customProfiles: profiles, ...(activeExists ? {} : { activeProfileId: DEFAULT_PROFILE_ID }) })
      },

      setNightMode: (mode) => set({ nightMode: mode }),
      setNightSchedule: (schedule) => set({ nightSchedule: schedule }),
      setLightMode: (on) => set({ lightMode: on }),
      setMotion: (motion) => set({ motion }),
      setCelebrationMode: (mode) => set({ celebrationMode: mode }),
      setCelebrationImageFileId: (fileId) => set({ celebrationImageFileId: fileId }),
      setSkin: (skin) => set({ skin }),
      setThickness: (thickness) => set({ thickness }),
      setSound: (on) => set({ sound: on }),
      setSoundVolume: (volume) => set({ soundVolume: Number.isFinite(volume) ? Math.min(1, Math.max(0, volume)) : 0 }),
    }),
    {
      name: 'quipwa-theme-storage',
      version: 2,
      migrate: migrateThemeState,

      partialize: (s): PersistedTheme => ({
        activeProfileId: s.activeProfileId,
        customProfiles: s.customProfiles,
        draft: s.draft,
        migratedToReplica: s.migratedToReplica,
        nightMode: s.nightMode,
        nightSchedule: s.nightSchedule,
        lightMode: s.lightMode,
        motion: s.motion,
        celebrationMode: s.celebrationMode,
        celebrationImageFileId: s.celebrationImageFileId,
        skin: s.skin,
        thickness: s.thickness,
        sound: s.sound,
        soundVolume: s.soundVolume,
      }),
    }
  )
)
